import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import toast from 'react-hot-toast';
import ModulePageHeading from '../../components/ModulePageHeading';
import DraftSignModal from '../../components/DraftSignModal';
import PaperworkBoard from '../../components/PaperworkBoard';
import {
  PAPERWORK_QUEUES,
  queueBadgeClass,
  resolvePaperworkNav,
} from '../../navigation/paperworkNavigation';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import { formatSsLabel } from '../../utils/formatSsLabel';

export default function AdminPaperworkOverview() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queue = searchParams.get('queue') || 'issue_waiting';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [signTarget, setSignTarget] = useState(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['paperwork-admin-overview', queue],
    queryFn: async () => (await api.get('/paperwork/admin/overview', { params: { queue } })).data?.data,
  });

  const takeMutation = useMutation({
    mutationFn: async (item) =>
      (
        await api.post('/paperwork/admin/tasks/take', {
          source_key: item.source_key,
          queue_type: item.queue_type,
          building_id: item.building_id,
          ss_code: item.ss_code || undefined,
          building_name: item.building_name,
          reason_text: item.reason_text,
          look_path: item.look_path,
          next_action: item.next_action,
          days_stuck: item.days_stuck,
        })
      ).data?.data,
    onSuccess: (task) => {
      toast.success(`Taken — ${task?.assignee_name || 'you'}`);
      queryClient.invalidateQueries({ queryKey: ['paperwork-admin-overview'] });
      queryClient.invalidateQueries({ queryKey: ['paperwork-admin-working'] });
      if (task?.id) navigate(`/paperwork/tasks/${task.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Take failed'),
  });

  const counts = data?.counts || {};
  const items = data?.items || [];

  const setQueue = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('queue', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolvePaperworkNav}
        title="Paperwork"
        infoTitle="Admin Paperwork"
        infoChildren={
          <div className="space-y-2 text-sm">
            <p>
              Under each building name, the SS we still have to handle, with the inspection term in
              parentheses (M, Q, H, Y). A bold contract name sits above each column: Full (our IQP),
              Partly (SS only), or External. Columns are confirm, repair and reinspection, and defect
              send. The last column is what the external company still has to do. Issued Form 12A /
              S-RaD are left off. A cell opens that building’s Paperwork page.
            </p>
            <p>
              <strong>Council processing</strong> counts packs already sent to council that are still
              waiting for Form 12 (BWOF) or B-RaD. Each lodgement adds one. Nothing sent yet means 0.
            </p>
            <p>
              <strong>Draft paper</strong> — Form HTML exists but IQP signature is missing. Sign here
              (or on the app) to complete Form 12A / S-RaD / Defect + inspection report.
            </p>
            <p>
              <strong>Ready to submit</strong> means every SS has a <em>signed</em> Form 12A or S-RaD
              (not Defect, not unsigned draft). Full agent: before council lodgement. Partial: before
              send to Main agent.
            </p>
          </div>
        }
      />

      <PaperworkBoard />

      <div className="mb-4 flex flex-wrap gap-2">
        {PAPERWORK_QUEUES.map((q) => {
          const n = counts[q.id];
          const on = queue === q.id;
          return (
            <button
              key={q.id}
              type="button"
              onClick={() => setQueue(q.id)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium ${
                on
                  ? 'border-primary-500 bg-primary-50 text-primary-900'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {q.label}
              {typeof n === 'number' ? (
                <span className="tabular-nums text-xs opacity-80">{n}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        {isLoading && <p className="px-4 py-8 text-sm text-gray-500">Loading queue…</p>}
        {isError && (
          <p className="px-4 py-8 text-sm text-red-600">
            {error?.response?.data?.error || 'Failed to load Overview'}
          </p>
        )}
        {!isLoading && !isError && (
          <ul className="divide-y">
            {items.map((item) => {
              const mine = item.taken && String(item.assignee_id) === String(user?.id);
              const isDraft = item.queue_type === 'draft_paper';
              return (
                <li
                  key={item.source_key}
                  className="px-4 py-3 flex flex-wrap items-center gap-3 hover:bg-slate-50/80"
                >
                  <span
                    className={`inline-block rounded border px-2 py-0.5 text-xs font-semibold shrink-0 ${queueBadgeClass(
                      item.queue_type
                    )}`}
                  >
                    {item.queue_label || item.queue_type}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.building_name || item.building_id}
                      {item.ss_code ? (
                        <>
                          {' · '}
                          {item.task_id ? (
                            <Link
                              to={`/paperwork/tasks/${item.task_id}?log=1`}
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary-700 underline decoration-primary-200 hover:decoration-primary-600"
                              title="Work log"
                            >
                              {formatSsLabel(item.ss_code)}
                            </Link>
                          ) : (
                            <span>{formatSsLabel(item.ss_code)}</span>
                          )}
                        </>
                      ) : item.ss_count ? (
                        ` · ${item.ss_count} SS`
                      ) : (
                        ''
                      )}
                      {item.days_stuck != null ? ` · ${item.days_stuck}d` : ''}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      Next: {item.next_action || '—'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isDraft ? (
                      <button
                        type="button"
                        onClick={() =>
                          setSignTarget({
                            buildingId: item.building_id,
                            ssCode: item.ss_code,
                            recordId: item.record_id,
                            label: `${item.building_name || ''} · ${formatSsLabel(item.ss_code || '')}`,
                          })
                        }
                        className="px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-semibold hover:bg-orange-700"
                      >
                        Sign
                      </button>
                    ) : null}
                    {item.taken ? (
                      mine && item.task_id ? (
                        <Link
                          to={`/paperwork/tasks/${item.task_id}`}
                          className="text-sm font-semibold text-primary-700 hover:underline"
                        >
                          Work on {item.assignee_name || 'you'}
                        </Link>
                      ) : (
                        <span className="text-xs font-semibold text-gray-600">
                          Work on {item.assignee_name || 'someone'}
                        </span>
                      )
                    ) : (
                      <button
                        type="button"
                        disabled={takeMutation.isPending}
                        onClick={() => takeMutation.mutate(item)}
                        className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 disabled:opacity-50"
                      >
                        Take it
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
            {!items.length && (
              <li className="px-4 py-10 text-center text-sm text-gray-500">
                Nothing in this queue right now.
              </li>
            )}
          </ul>
        )}
      </div>

      {signTarget ? (
        <DraftSignModal
          buildingId={signTarget.buildingId}
          ssCode={signTarget.ssCode}
          recordId={signTarget.recordId}
          label={signTarget.label}
          onClose={() => setSignTarget(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['paperwork-admin-overview'] });
          }}
        />
      ) : null}
    </div>
  );
}
